import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Mutex } from 'async-mutex';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FileStorageServiceInterface } from '../../interfaces/file-storage.interface';
import { isFileError } from '../../utilities/is-file-error';
import { LZStringService } from '../l-z-string/l-z-string.service';

@Injectable()
export abstract class BaseStorageService implements FileStorageServiceInterface {
    protected constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        protected readonly LZString: LZStringService,
    ) {}

    public checkCompressedData(mutex: Mutex, storageId: string): Observable<boolean> {
        return this.checkFile$(mutex, storageId);
    }

    /**
     * This is a wrapper function used to convert the value returned by checkFile() function from Promise to observable.
     *
     * @param mutex
     * @param fileName
     */
    private checkFile$(mutex: Mutex, fileName: string): Observable<boolean> {
        return from(this.checkFile(mutex, fileName)).pipe(
            catchError((error) => {
                // if the file was not cached and we get an NOT_FOUND_ERR, then return 'false' instead of passing
                // the error on
                const suppressError = isFileError(error) && error.code === 1;

                if (!suppressError) {
                    this.log.error(
                        `BaseStorageService: checkFile$: Error when checking for file '${fileName}': ${JSON.stringify(
                            error,
                        )}`,
                    );
                    return throwError(error);
                }
                return of(false);
            }),
        );
    }

    /**
     * This function is used to avoid race condition while checking from the local storage.
     * This is done with the help of mutex, which locks the file if it is available and unlocks it once the transaction is successful.
     */
    protected abstract checkFile(mutex: Mutex, fileName: string): Promise<boolean>;

    /**
     * Function that loads compressed data from indexedDB file store.
     *
     * @param mutex
     * @param storageId
     */
    public loadCompressedData<T>(mutex: Mutex, storageId: string): Observable<T> {
        this.log.debug(`BaseStorageService: loadCompressedData: loading data from "${storageId}"`);
        return this.readAsArrayBuffer$(mutex, storageId).pipe(
            map((response) => {
                const compressedData = response.buffer;
                const uint8array = new Uint8Array(compressedData);
                const dataString = this.LZString.decompressFromUint8Array(uint8array);
                return JSON.parse(dataString) as T;
            }),
        );
    }

    /**
     * Function that loads compressed data from indexedDB file store.
     *
     * @param mutex
     * @param storageId
     */
    public loadCompressedDataWithTimestamp<T>(
        mutex: Mutex,
        storageId: string,
    ): Observable<{ data: T; timestamp: number }> {
        this.log.debug(`BaseStorageService: loadCompressedData: loading data from "${storageId}"`);
        return this.readAsArrayBuffer$(mutex, storageId).pipe(
            map((response) => {
                const compressedData = response.buffer;
                const timestamp = response.timestamp;
                const uint8array = new Uint8Array(compressedData);
                const dataString = this.LZString.decompressFromUint8Array(uint8array);
                const data = JSON.parse(dataString) as T;
                return { data, timestamp };
            }),
        );
    }

    /**
     * This is a wrapper function used to convert the value returned by readAsArrayBuffer() function from Promise to observable.
     *
     * @param mutex
     * @param fileName
     */
    private readAsArrayBuffer$(mutex: Mutex, fileName: string): Observable<{ buffer: ArrayBuffer; timestamp: number }> {
        return from(this.readAsArrayBuffer(mutex, fileName)).pipe(
            catchError((error) => {
                const isFileNotFound = isFileError(error) && error.code === 1;
                if (isFileNotFound) {
                    this.log.warn(
                        `BaseStorageService: readAsArrayBuffer$: Tried reading '${fileName}', but it does not exist.`,
                    );
                } else {
                    this.log.error(
                        `BaseStorageService: readAsArrayBuffer$: Failed to read stored data from file '${fileName}'. Error Message: ${JSON.stringify(
                            error,
                        )}`,
                    );
                }
                return throwError(error);
            }),
        );
    }

    /**
     * This function is used to avoid race condition while getting data from the local storage.
     * This is done with the help of mutex, which locks the file if it is available and unlocks it once the transaction is successful.
     */
    protected abstract readAsArrayBuffer(
        mutex: Mutex,
        fileName: string,
    ): Promise<{ buffer: ArrayBuffer; timestamp: number }>;

    public removeCompressedData(mutex: Mutex, storageId: string): Observable<void> {
        return this.removeFile$(mutex, storageId);
    }

    /**
     * This is a wrapper function used to convert the value returned by removeFile() function from Promise to observable.
     *
     * @param mutex
     * @param fileName
     */
    private removeFile$(mutex: Mutex, fileName: string): Observable<void> {
        return from(this.removeFile(mutex, fileName)).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    /**
     * This function is used to avoid race condition while removing file from the storage.
     * This is done with the help of mutex, which locks the file if it is available and unlocks it once the transaction is successful.
     */
    protected abstract removeFile(mutex: Mutex, fileName: string): Promise<void>;

    public storeCompressedData<T>(
        mutex: Mutex,
        storageId: string,
        data: T,
        timestamp: number = Date.now(),
    ): Observable<T> {
        this.log.debug(`BaseStorageService: storeCompressedData: writing data to "${storageId}"`);
        return this.writeFile$(mutex, storageId, data, timestamp);
    }

    /**
     * This is a wrapper function used to convert the value returned by writeFile() function from Promise to observable.
     *
     * @param mutex
     * @param fileName
     * @param data
     * @param timestamp
     */
    private writeFile$<T>(mutex: Mutex, fileName: string, data: T, timestamp: number = Date.now()): Observable<T> {
        const json = JSON.stringify(data);
        const compressed = this.LZString.compressToUint8Array(json);

        return from(this.writeFile(mutex, fileName, compressed, timestamp)).pipe(
            catchError((error) => {
                this.log.error(
                    `BaseStorageService: writeFile$: Failed saving data to file '${fileName}'. Error Message: ${JSON.stringify(
                        error,
                    )}`,
                );
                return throwError(error);
            }),
            map(() => data),
        );
    }

    /**
     * This function is used to avoid race condition while saving data in the local storage.
     * This is done with the help of mutex, which locks the file if it is available and unlocks it once the transaction is successful.
     * The mutex is provided by the calling service to guard the access to the specific file according to the needs of the calling service.
     * The file storage service just encapsulates the logic of accessing the file, but does not care about locking logic.
     */
    protected abstract writeFile(mutex: Mutex, fileName: string, content: Uint8Array, timestamp: number): Promise<void>;
}
